import { FC, useContext, useEffect, useState, useRef } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import { HrailLabelLanguage } from "../../../../../http/hrail/types/labelLanguages";
import { useHrailLabelLanguagesApi } from "../../../../../http/hrail/useLabelLanguages";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface LabelLanguageFormBody {
  code: string;
  name: string;
  extraItem: SelectOption | null;
  factorPerLabel: number | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

type SelectOption = {
  id: string;
  label: string;
};

interface LabelLanguageFormProps {
  labelLanguage?: HrailLabelLanguage;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}

const LabelLanguageForm: FC<LabelLanguageFormProps> = ({ labelLanguage, isDisabled, isLoading, onClose, onCreated, onSaved }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const labelLanguageApi = useHrailLabelLanguagesApi();
  const itemsApi = useItemsApi();
  const [itemOptions, setItemOptions] = useState<SelectOption[]>([]);
  const itemAborter = useRef(new AbortController());
  const itemSearchTerm = useRef("");
  const loadItemOptions = createLoadOptions(itemAborter, itemSearchTerm, (query, signal) =>
    itemsApi.getAdminItems(query, signal).then((items) => items.map(toItemOption)),
  );

  const defaultValues: LabelLanguageFormBody = {
    code: labelLanguage?.code ?? "",
    name: labelLanguage?.name ?? "",
    extraItem: labelLanguage?.extraItem ? toItemOption(labelLanguage.extraItem) : null,
    factorPerLabel: labelLanguage?.factorPerLabel ?? null,
    order: labelLanguage?.order ?? 50,
    visibility: labelLanguage?.visibility ?? "internal",
  };

  useEffect(() => {
    loadItemOptions("").then(setItemOptions).catch(() => setItemOptions([]));
  }, []);

  const onSubmit = () => async (formData: LabelLanguageFormBody) => {
    setIsSubmitting(true);

    try {
      if (labelLanguage) {
        await labelLanguageApi.updateAdminHrailLabelLanguage(labelLanguage.id, {
  code: formData.code ?? "",
  name: formData.name ?? "",
  extraItemId: formData.extraItem?.id ?? null,
  factorPerLabel: typeof formData.factorPerLabel === "number" ? formData.factorPerLabel : null,
  order: formData.order ?? 50,
  visibility: formData.visibility,
});
        dispatch(showSuccessNotification(i18n.translation));
        onSaved?.();
        onClose?.();
      } else {
        const res = await labelLanguageApi.insertAdminHrailLabelLanguage({
  code: formData.code ?? "",
  name: formData.name ?? "",
  extraItemId: formData.extraItem?.id ?? null,
  factorPerLabel: typeof formData.factorPerLabel === "number" ? formData.factorPerLabel : null,
  order: formData.order ?? 50,
  visibility: formData.visibility,
});
        dispatch(showSuccessNotification(i18n.translation));
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      }
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<LabelLanguageFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, formState: { errors }, register, reset, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [labelLanguage]);

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.code} error={errors.code} isOptional={false}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading || isSubmitting}
                  {...register("code", { required: true, minLength: 1 })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.name} error={errors.name} isOptional={false}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading || isSubmitting}
                  {...register("name", { required: true, minLength: 1 })}
                />
              )}
            </FormField>
            <Controller
              name="extraItem"
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.extraItem} error={fieldState.error} isOptional={true}>
                  {({ labelId, isInvalid }) => (
                    <CustomAsyncSelect<SelectOption>
                      {...field}
                      inputId={labelId}
                      defaultOptions={itemOptions}
                      loadOptions={loadItemOptions}
                      isClearable={true}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.id}
                      isInvalid={isInvalid}
                      onChange={(value) => setValue("extraItem", value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="factorPerLabel"
              control={control}
              rules={{ required: false, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.factorPerLabel} error={fieldState.error} isOptional={true}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={2}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("factorPerLabel", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

type ItemOptionSource = { id: string; code?: string | null; description?: string | null; name?: string | null };

const toItemOption = (item: ItemOptionSource): SelectOption => ({ id: item.id, label: itemLabel(item) });

export default LabelLanguageForm;
